/**
 * Named colour palette — the single source of truth for every colour in the app.
 *
 * Two layers, following the house pattern:
 *
 * 1. **Primitives** (`Color`) — the literal values from the Elyxeer brand guide.
 *    Three families of four steps: Magic Violet, Science Silver, Stem Black.
 *    Reference them by name (`Color.Violet700`) instead of pasting hex around.
 * 2. **Semantic tokens** (`ThemeColor`) — names whose *value depends on the active
 *    colour scheme* (`surface`, `textPrimary`, `accent`). Use these for anything
 *    that must work in both light and dark.
 *
 * `palette.ts` derives the MUI palette from these, and `useColor()` resolves either
 * layer to a concrete string for canvas/SVG/inline styles that cannot read CSS vars.
 *
 * Every pairing used for text or a control below meets WCAG AA (4.5:1); the measured
 * ratio is noted wherever the choice is not obvious.
 */

// ── Static primitives ─────────────────────────────────────────────
export const Color = {
  // Magic Violet — the brand colour.
  Violet900: "violet900",
  Violet700: "violet700",
  Violet500: "violet500",
  Violet300: "violet300",

  // Science Silver — surfaces.
  Silver700: "silver700",
  Silver500: "silver500",
  Silver300: "silver300",
  Silver100: "silver100",

  // Stem Black — text and dark surfaces.
  Black900: "black900",
  Black700: "black700",
  Black500: "black500",
  Black300: "black300",

  White: "white",

  /**
   * Dark-mode-only tint. Magic Violet is too dark to read as text on Stem Black — the
   * lightest brand step (#a07ce5) reaches only 4.33:1 on a raised dark surface and
   * 2.79:1 on an inset one. This tint clears AA on all three dark surfaces
   * (8.44:1 / 7.41:1 / 4.78:1). It exists solely so violet can be used for text and
   * icons in dark mode; fills still use the unmodified brand violet.
   */
  VioletTextDark: "violetTextDark",
} as const;

export type Color = (typeof Color)[keyof typeof Color];

/** Concrete hex for every static primitive. */
export const colors: Record<Color, string> = {
  [Color.Violet900]: "#5d27c4",
  [Color.Violet700]: "#713ad8",
  [Color.Violet500]: "#885bde",
  [Color.Violet300]: "#a07ce5",

  [Color.Silver700]: "#b7b6ba",
  [Color.Silver500]: "#cbcacd",
  [Color.Silver300]: "#dfdee0",
  [Color.Silver100]: "#f3f3f3",

  [Color.Black900]: "#222222",
  [Color.Black700]: "#2c2c2c",
  [Color.Black500]: "#494949",
  [Color.Black300]: "#676767",

  [Color.White]: "#ffffff",

  [Color.VioletTextDark]: "#c7b3f1",
};

// ── Semantic tokens ───────────────────────────────────────────────
export const ThemeColor = {
  Background: "background",
  /**
   * The darkest stop of the page gradient. `background` is only the *lightest* end of
   * it, so text further down the page actually sits on this — which is what the
   * contrast guard measures against.
   */
  BackgroundDeep: "backgroundDeep",
  Surface: "surface",
  SurfaceInset: "surfaceInset",
  TextPrimary: "textPrimary",
  TextSecondary: "textSecondary",
  TextMuted: "textMuted",
  Border: "border",
  /** Fill for primary controls. Identical in both schemes — white on it is 6.37:1. */
  Accent: "accent",
  /** Label that sits on top of {@link ThemeColor.Accent}. */
  OnAccent: "onAccent",
  /** Pressed / active state of a primary control. */
  AccentPressed: "accentPressed",
  /** Violet used as *text or icon*, which needs more contrast than a fill. */
  AccentText: "accentText",
  /**
   * Scrim for a chip or label that sits *on artwork* — a cover image the app does
   * not control. Identical in both schemes for that reason: the thing behind it is
   * a photograph, not a page surface, so there is no scheme to follow.
   */
  MediaScrim: "mediaScrim",
  /** Label that sits on top of {@link ThemeColor.MediaScrim}. */
  OnMediaScrim: "onMediaScrim",
  /** Morning phase of the circadian ritual. */
  Morning: "morning",
  /** Evening phase of the circadian ritual. */
  Evening: "evening",
} as const;

export type ThemeColor = (typeof ThemeColor)[keyof typeof ThemeColor];

/** Resolved value of every semantic token, per colour scheme. */
export const themeColors: Record<
  "light" | "dark",
  Record<ThemeColor, string>
> = {
  light: {
    [ThemeColor.Background]: colors[Color.Silver100],
    [ThemeColor.BackgroundDeep]: colors[Color.Silver500],
    [ThemeColor.Surface]: colors[Color.White],
    [ThemeColor.SurfaceInset]: colors[Color.Silver300],
    // Worst case is the gradient's dark end: 9.75:1.
    [ThemeColor.TextPrimary]: colors[Color.Black900],
    [ThemeColor.TextSecondary]: colors[Color.Black700], // 8.56:1 worst case
    [ThemeColor.TextMuted]: colors[Color.Black500], // 5.52:1 worst case
    [ThemeColor.Border]: "rgba(34, 34, 34, 0.12)",
    [ThemeColor.Accent]: colors[Color.Violet700],
    [ThemeColor.OnAccent]: colors[Color.White],
    [ThemeColor.AccentPressed]: colors[Color.Violet900],
    // The base brand violet as link text: 7.41:1 on the page background.
    [ThemeColor.AccentText]: colors[Color.Violet900],
    // 0.72 alpha over arbitrary artwork, so the guarded pairing below measures the
    // opaque worst case: silver.100 on stem black is 14.68:1 with room to spare.
    [ThemeColor.MediaScrim]: "rgba(34, 34, 34, 0.72)",
    [ThemeColor.OnMediaScrim]: colors[Color.Silver100],
    [ThemeColor.Morning]: colors[Color.Violet500],
    [ThemeColor.Evening]: colors[Color.Violet900],
  },
  dark: {
    [ThemeColor.Background]: colors[Color.Black900],
    [ThemeColor.BackgroundDeep]: colors[Color.Black700],
    [ThemeColor.Surface]: colors[Color.Black700],
    [ThemeColor.SurfaceInset]: colors[Color.Black500],
    // Worst case is the inset surface: silver.700 reaches only 4.46:1 there, so the
    // tiers stop one step lighter than the palette allows.
    [ThemeColor.TextPrimary]: colors[Color.Silver100], // 8.11:1 worst case
    [ThemeColor.TextSecondary]: colors[Color.Silver300], // 6.71:1 worst case
    [ThemeColor.TextMuted]: colors[Color.Silver500], // 5.52:1 worst case
    [ThemeColor.Border]: "rgba(255, 255, 255, 0.12)",
    // Unchanged from light: the violet fill reads correctly on both page colours
    // and keeps the primary action recognisably the same control.
    [ThemeColor.Accent]: colors[Color.Violet700],
    [ThemeColor.OnAccent]: colors[Color.White],
    [ThemeColor.AccentPressed]: colors[Color.Violet500],
    [ThemeColor.AccentText]: colors[Color.VioletTextDark], // 4.78:1 worst case
    [ThemeColor.MediaScrim]: "rgba(34, 34, 34, 0.72)",
    [ThemeColor.OnMediaScrim]: colors[Color.Silver100],
    [ThemeColor.Morning]: colors[Color.Violet300],
    [ThemeColor.Evening]: colors[Color.VioletTextDark],
  },
};

// ── Resolution helpers ────────────────────────────────────────────
export type ColorScheme = "light" | "dark";

/** Anything `useColor` accepts: a semantic token, a named primitive, transparent. */
export type ValidColor = Color | ThemeColor | "transparent";

const themeColorValues = new Set<string>(Object.values(ThemeColor));

/** True when `color` is a scheme-dependent semantic token, not a static primitive. */
export function isThemeColor(color: string): color is ThemeColor {
  return themeColorValues.has(color);
}

/**
 * Resolve any {@link ValidColor} to a concrete CSS colour for `scheme`.
 * Unknown values pass through unchanged, so a raw hex/rgb string is always safe.
 */
export function resolveColor(
  color: ValidColor | (string & {}) | undefined,
  scheme: ColorScheme,
): string {
  if (!color || color === "transparent") return "transparent";
  if (isThemeColor(color)) return themeColors[scheme][color];
  return colors[color as Color] ?? color;
}

/** Append an alpha channel to a six-digit hex value. */
export function withAlpha(hex: string, opacity: number): string {
  const alpha = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, "0");
  return `${hex}${alpha}`;
}
